import React from 'react';
import { Link } from 'react-router-dom';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { mapAfter } from 'apollo-relay/update-query';
import Helmet from 'helmet/helmet';
import { toGlobalId } from 'graphql-relay';

import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import LoadingProgress from 'lib/react-router/loading-progress';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import ErrorPage from 'four-nets/errors/error-page';
import OrderFilterView from 'four-nets/bazaar/product-lists/order-filter-view';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';
import { Site } from 'four-nets/site';

import Vendor from '../vendor/vendor';
import CatalogNavigationLoader from '../catalog/catalog-navigation/catalog-navigation-loader';
import VendorCountyFilter from './vendor-county-filter/vendor-county-filter';

import query, { CatalogCategoriesQuery, CatalogCategoriesQueryVariables } from './vendors.graphql';
import { VendorOrderingFilterEnum } from './vendors.graphql.types';
import defaultStyles from './vendors.scss';
import { HideForVendor } from '../permissions';

interface Params {
    slug: string;
    orderBy?: string;
}

interface Search {
    county?: string | string[];
    orderBy?: string;
    offset?: number;
}

export interface Props extends RouteWithSearchRenderProps<Params, Search> {
    styles: typeof defaultStyles;
}

const FIRST = 10;

const Vendors: React.FC<Props> = (props, context) => {
    const {
        styles,
        match,
        search,
        location: { pathname },
    } = props;
    const { slug } = match.params;
    const { orderBy, offset, county } = search;

    let after: string | undefined;
    if (offset) {
        after = btoa(`arrayconnection:${parseInt(offset as any, 10) - 1}`);
    }
    let countyIds: string[] = [];
    if (county) {
        countyIds = Array.isArray(county) ? county : [county];
    }
    const globalCountyIds = countyIds.map(id => toGlobalId('CountyNode', id));

    const { data, loading, error, fetchMore } = useQuery<CatalogCategoriesQuery, CatalogCategoriesQueryVariables>(
        query,
        {
            variables: {
                categorySlug: slug,
                countyIds: globalCountyIds,
                orderBy: (orderBy || VendorOrderingFilterEnum.RANDOM) as VendorOrderingFilterEnum,
                after,
            },
            errorPolicy: 'all',
            fetchPolicy: 'cache-first',
        }
    );

    const renderHelmet = (H: React.ComponentClass) => {
        const site = context && (context.site as Site);
        const catalogCategory =
            data &&
            data.catalogCategories &&
            data.catalogCategories.edges &&
            data.catalogCategories.edges.length === 1 &&
            data.catalogCategories.edges[0]!.node;

        const counties = data?.counties?.edges;

        const selectedCounties: string[] = [];
        if (countyIds && counties) {
            counties.forEach(edge => {
                if (edge && edge.node && globalCountyIds.indexOf(edge.node.id) > -1) {
                    selectedCounties.push(edge.node.name);
                }
            });
        }

        const namePart = catalogCategory ? catalogCategory.name : '';
        const countiesPart = selectedCounties.length ? ` - ${selectedCounties.join(', ')}` : '';

        return (
            <H>
                <title>{`${namePart}${countiesPart} | ${site.name}`}</title>
            </H>
        );
    };

    const changeOrder = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { location, history } = props;

        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                orderBy: event.target.value,
            })}`,
        });
    };

    const catalogCategory =
        data &&
        data.catalogCategories &&
        data.catalogCategories.edges &&
        data.catalogCategories.edges.length === 1 &&
        data.catalogCategories.edges[0]!.node;

    if (error) {
        (error as any).code = 404;
        return <ErrorPage error={error} />;
    }

    if (!catalogCategory) {
        return null;
    }

    const endCursor = catalogCategory.vendors && catalogCategory.vendors.pageInfo!.endCursor;

    const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
        endCursor,
        pathname,
        pageSize: FIRST,
    });

    let nextOffset;
    if (endCursor) {
        nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
    } else {
        nextOffset = offset;
    }

    return (
        <LoadingProgress loading={loading}>
            <ErrorPage error={error}>
                <>
                    <Helmet render={renderHelmet} />

                    <div className={styles.headerDiv}>
                        <h1 className={styles.title}>{catalogCategory.name}</h1>
                        <HideForVendor>
                            <Link to='/pro' className={styles.registerLink}>
                                <FormattedMessage id='ADD_VENDOR' />
                            </Link>
                        </HideForVendor>
                    </div>
                    <div className={styles.container}>
                        <div className={styles.leftColumn}>
                            <CatalogNavigationLoader mini={true} slug={slug} hideNotSelected={true} />
                        </div>
                        <div className={styles.rightColumn}>
                            <div className={styles.filterDiv}>
                                <VendorCountyFilter counties={data && data.counties} countyIds={countyIds} />
                                <OrderFilterView
                                    style={{ select: styles.orderFilterView }}
                                    orderBy={orderBy ? orderBy : 'RANDOM'}
                                    onChange={event => changeOrder(event)}
                                >
                                    <FormattedMessage id='VENDORS_ORDER_RANDOM'>
                                        {msg => <option value={VendorOrderingFilterEnum.RANDOM}>{msg}</option>}
                                    </FormattedMessage>
                                    <FormattedMessage id='VENDORS_ORDER_LIKES_COUNT'>
                                        {msg => (
                                            <option value={VendorOrderingFilterEnum.LIKES_COUNT_DESC}>{msg}</option>
                                        )}
                                    </FormattedMessage>
                                    {context && context.site && (context.site as Site).isWedding ? (
                                        <FormattedMessage id='ORDER_BY_USER_BRAND_COUNT'>
                                            {msg => (
                                                <option value={VendorOrderingFilterEnum.USER_BRAND_COUNT_DESC}>
                                                    {msg}
                                                </option>
                                            )}
                                        </FormattedMessage>
                                    ) : null}

                                    <FormattedMessage id='VENDORS_ORDER_CREATED_TIME_ASC'>
                                        {msg => <option value={VendorOrderingFilterEnum.CREATED_TIME}>{msg}</option>}
                                    </FormattedMessage>
                                    <FormattedMessage id='VENDORS_ORDER_CREATED_TIME_DESC'>
                                        {msg => (
                                            <option value={VendorOrderingFilterEnum.CREATED_TIME_DESC}>{msg}</option>
                                        )}
                                    </FormattedMessage>
                                </OrderFilterView>
                            </div>
                            <ul className={styles.list}>
                                {catalogCategory.vendors
                                    ? catalogCategory.vendors.edges.map(vendor =>
                                          vendor && vendor.node && vendor.node ? (
                                              <li className={styles.vendorItem} key={vendor.node.id}>
                                                  <Vendor {...vendor.node} />
                                              </li>
                                          ) : null
                                      )
                                    : null}
                            </ul>
                            {catalogCategory.vendors && catalogCategory.vendors.pageInfo.hasNextPage ? (
                                <FetchMoreButton
                                    disabled={loading}
                                    href={endCursorHref}
                                    prevHref={prevCursorHref}
                                    onClick={() => {
                                        return fetchMore({
                                            variables: {
                                                after: endCursor,
                                                first: FIRST,
                                            },
                                            updateQuery: (
                                                previousResult: CatalogCategoriesQuery = {} as CatalogCategoriesQuery,
                                                {
                                                    fetchMoreResult = {} as CatalogCategoriesQuery,
                                                }: {
                                                    fetchMoreResult?: CatalogCategoriesQuery;
                                                }
                                            ) => {
                                                return {
                                                    ...previousResult,
                                                    ...fetchMoreResult,
                                                    catalogCategories: {
                                                        ...fetchMoreResult.catalogCategories,
                                                        edges: [
                                                            {
                                                                ...fetchMoreResult!.catalogCategories!.edges[0]!,
                                                                node: {
                                                                    ...fetchMoreResult!.catalogCategories!.edges[0]!
                                                                        .node!,
                                                                    vendors: mapAfter(
                                                                        previousResult!.catalogCategories!.edges[0]!
                                                                            .node!.vendors,
                                                                        fetchMoreResult!.catalogCategories!.edges[0]!
                                                                            .node!.vendors
                                                                    ),
                                                                },
                                                            },
                                                        ],
                                                    },
                                                } as CatalogCategoriesQuery;
                                            },
                                        });
                                    }}
                                />
                            ) : null}
                            {(catalogCategory.vendors && catalogCategory.vendors.pageInfo.hasNextPage) || offset ? (
                                <OffsetPager pageSize={FIRST} offset={nextOffset} />
                            ) : null}
                        </div>
                    </div>
                </>
            </ErrorPage>
        </LoadingProgress>
    );
};

Vendors.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(Vendors);
